import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useMediaQuery } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import SoftBox from "../../components/SoftBox";
import SoftInput from "../../components/SoftInput";
import SoftButton from "../../components/SoftButton";
import SoftTypography from "../../components/SoftTypography";
import PageHeader from "../../components/PageHeader";
import GreenToggle from "../../components/GreenToggle";
import { Skeleton } from "../../components/shared/Loading";

import {
  GET_BID_QUALITY_NOTIFICATION_SETTINGS,
  UPDATE_BID_QUALITY_NOTIFICATION_SETTINGS,
} from "../../api/kpi";
import { toErrorMessage } from "../../helpers/errorText";

import { T, FONT, MICRO_LABEL_SX } from "./bidQualityTokens";
import { BidQualityTabs, SectionCard, Notice } from "./BidQualityChrome";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 10;

const inputFieldStyle = {
  borderRadius: "8px",
  border: `1px solid ${T.fieldLine}`,
  backgroundColor: "#F6F7F9 !important",
  color: "#1d2939 !important",
  minHeight: "44px",
};

/** One removable recipient chip. */
const RecipientChip = ({ email, onRemove }) => (
  <SoftBox
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      height: "30px",
      padding: "0 6px 0 12px",
      borderRadius: "999px",
      backgroundColor: T.field,
      border: `1px solid ${T.fieldLine}`,
      fontFamily: FONT,
      fontSize: "13px",
      fontWeight: 500,
      color: T.ink2,
    }}
  >
    {email}
    <SoftBox
      component="button"
      type="button"
      onClick={() => onRemove(email)}
      aria-label={`Remove ${email}`}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "20px",
        height: "20px",
        border: "none",
        borderRadius: "50%",
        background: "transparent",
        color: T.faint,
        cursor: "pointer",
        "&:hover": { backgroundColor: T.hairline, color: T.muted },
      }}
    >
      <CloseRoundedIcon sx={{ fontSize: "0.95rem" }} />
    </SoftBox>
  </SoftBox>
);

/**
 * Admin-only settings panel: where to send an email when a technician
 * submits a Job Bid Quality score. The server sends the email on every
 * submission when the toggle is on and at least one recipient is saved.
 */
const BidQualityNotificationSettings = () => {
  const isMobile = useMediaQuery((t) => t.breakpoints.down("sm"));

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const savedRef = useRef({ enabled: false, recipients: [] });

  const load = () => {
    setLoading(true);
    setLoadError("");
    GET_BID_QUALITY_NOTIFICATION_SETTINGS()
      .then((res) => {
        const data = res?.data ?? res;
        const nextEnabled = Boolean(data?.enabled);
        const nextRecipients = Array.isArray(data?.recipientEmails) ? data.recipientEmails : [];
        setEnabled(nextEnabled);
        setRecipients(nextRecipients);
        savedRef.current = { enabled: nextEnabled, recipients: nextRecipients };
        setDirty(false);
      })
      .catch((err) => setLoadError(toErrorMessage(err, "Could not load notification settings")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markDirty = (nextEnabled, nextRecipients) => {
    const saved = savedRef.current;
    const changed =
      nextEnabled !== saved.enabled ||
      nextRecipients.length !== saved.recipients.length ||
      nextRecipients.some((e, i) => e !== saved.recipients[i]);
    setDirty(changed);
  };

  const toggleEnabled = () => {
    const next = !enabled;
    setEnabled(next);
    markDirty(next, recipients);
  };

  const addRecipient = () => {
    const candidate = draft.trim().toLowerCase();
    if (!candidate) return;
    if (!EMAIL_REGEX.test(candidate)) {
      setDraftError("That doesn't look like a valid email address.");
      return;
    }
    if (recipients.includes(candidate)) {
      setDraftError("That email is already on the list.");
      return;
    }
    if (recipients.length >= MAX_RECIPIENTS) {
      setDraftError(`You can add at most ${MAX_RECIPIENTS} recipients.`);
      return;
    }
    const next = [...recipients, candidate];
    setRecipients(next);
    setDraft("");
    setDraftError("");
    markDirty(enabled, next);
  };

  const removeRecipient = (email) => {
    const next = recipients.filter((e) => e !== email);
    setRecipients(next);
    markDirty(enabled, next);
  };

  const handleDraftKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addRecipient();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const toastId = toast.loading("Saving...");
    try {
      const result = await UPDATE_BID_QUALITY_NOTIFICATION_SETTINGS({
        enabled,
        recipientEmails: recipients,
      });
      if (result?.error) throw new Error(result.error.message);
      toast.success("Notification settings saved", { id: toastId });
      savedRef.current = { enabled, recipients };
      setDirty(false);
    } catch (err) {
      toast.error(toErrorMessage(err, "Could not save"), { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const emptyRecipients = !loading && recipients.length === 0;

  const body = useMemo(() => {
    if (loading) {
      return (
        <SoftBox sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Skeleton width="40%" height={20} />
          <Skeleton width="70%" />
          <Skeleton width="55%" />
        </SoftBox>
      );
    }
    if (loadError) {
      return (
        <Notice
          tone="bad"
          title="Couldn't load notification settings"
          action={
            <SoftButton
              variant="outlined"
              color="primary"
              size="small"
              onClick={load}
              sx={{ fontFamily: FONT, textTransform: "none", borderRadius: "8px", fontWeight: 600, whiteSpace: "nowrap" }}
            >
              Retry
            </SoftButton>
          }
        >
          {loadError}
        </Notice>
      );
    }
    return (
      <>
        <SoftBox sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" }}>
          <SoftBox sx={{ minWidth: 0 }}>
            <SoftTypography sx={{ fontFamily: FONT, fontSize: "14px", fontWeight: 600, color: T.ink }}>
              Email these people on every submission
            </SoftTypography>
            <SoftTypography sx={{ fontFamily: FONT, fontSize: "12.5px", fontWeight: 400, color: T.muted, mt: "2px", lineHeight: 1.5 }}>
              Each new score is emailed to everyone below as soon as it's submitted.
            </SoftTypography>
          </SoftBox>
          <GreenToggle checked={enabled} onChange={toggleEnabled} ariaLabel="Enable Bid Quality email notifications" />
        </SoftBox>

        <SoftBox sx={{ marginTop: "20px" }}>
          <SoftTypography component="label" sx={{ ...MICRO_LABEL_SX, display: "block", marginBottom: "8px" }}>
            Recipients
          </SoftTypography>

          {recipients.length > 0 && (
            <SoftBox sx={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
              {recipients.map((email) => (
                <RecipientChip key={email} email={email} onRemove={removeRecipient} />
              ))}
            </SoftBox>
          )}

          <SoftBox sx={{ display: "flex", gap: "10px", flexDirection: { mini: "column", sm: "row" } }}>
            {/* SoftInput nests its field in a plain div, so flex: 1 on the field
                itself never reached the flex row — the box carries the growth. */}
            <SoftBox sx={{ flex: 1, minWidth: 0 }}>
              <SoftInput
                fullWidth
                type="email"
                placeholder="name@company.com"
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (draftError) setDraftError("");
                }}
                onKeyDown={handleDraftKeyDown}
                sx={{ ...inputFieldStyle, width: "100%", ...(draftError && { borderColor: "#D92D20" }) }}
              />
            </SoftBox>
            <SoftButton
              variant="outlined"
              color="primary"
              onClick={addRecipient}
              disabled={!draft.trim() || recipients.length >= MAX_RECIPIENTS}
              sx={{ fontFamily: FONT, textTransform: "none", borderRadius: "8px", fontWeight: 600, whiteSpace: "nowrap" }}
            >
              Add
            </SoftButton>
          </SoftBox>
          {draftError && (
            <SoftTypography sx={{ fontFamily: FONT, fontSize: "12px", fontWeight: 600, color: "#D92D20", marginTop: "6px" }}>
              {draftError}
            </SoftTypography>
          )}
          {!draftError && (
            <SoftTypography sx={{ ...MICRO_LABEL_SX, fontSize: "11.5px", marginTop: "6px" }}>
              Press Enter to add. Up to {MAX_RECIPIENTS} recipients.
            </SoftTypography>
          )}
          {emptyRecipients && (
            <SoftBox sx={{ marginTop: "12px" }}>
              <Notice tone="neutral" title="No recipients yet">
                Add at least one email address before turning this on.
              </Notice>
            </SoftBox>
          )}
        </SoftBox>

        <SoftBox sx={{ display: "flex", justifyContent: "flex-end", marginTop: "24px", paddingTop: "20px", borderTop: `1px solid ${T.hairline}` }}>
          <SoftButton
            variant="gradient"
            color="primary"
            onClick={handleSave}
            disabled={saving || !dirty}
            sx={{ fontFamily: FONT, textTransform: "none", borderRadius: "10px", fontWeight: 600, minWidth: "140px" }}
          >
            {saving ? "Saving..." : "Save changes"}
          </SoftButton>
        </SoftBox>
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadError, enabled, recipients, draft, draftError, saving, dirty]);

  return (
    <>
      <PageHeader
        headerText="Job Bid Quality"
        subheaderText="Choose who gets emailed when a technician rates a bid."
        isMobile={isMobile}
        tabs={<BidQualityTabs active="settings" />}
      />

      <SoftBox sx={{ margin: { mini: "0.5rem 1rem 1rem 1rem", sm: "1rem 1.5rem 1.5rem 1.5rem" }, maxWidth: "760px" }}>
        <SectionCard
          title="Email notifications"
          caption="Sent from info@gamemybiz.com"
          bodySx={{ padding: "20px" }}
        >
          {body}
        </SectionCard>

      </SoftBox>
    </>
  );
};

export default BidQualityNotificationSettings;
